import { Vue, Ref, Prop, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { BModal } from 'bootstrap-vue/esm/components/modal';
import { TimelapseConfiguration } from '@/models/timelapseconfiguration';
import TimelapseFilter from '@/components/timelapsefilter/timelapsefilter.component.vue';
import TimelapseExclusion from '@/components/timelapseexclusion/timelapseexclusion.component.vue';
import TimelapseSettings from '@/components/timelapsesettings/timelapsesettings.component.vue';
import { Camera } from '@/models/camera';
import S3Image from '@/components/s3-image/S3Image.vue';
import { Scenario } from '@/models/scenario';
import { User } from '@/models/user';
import { Action } from 'vuex-class';
import {
  ScenarioConfigurationCreateRequest,
  ScenarioConfigurationUpdateRequest,
} from '@/services/projects/projects.interfaces';
import { Project } from '@/models/project';
import { Position, Crop } from '@/models/enums';
// @ts-ignore pas de d.ts pour la build dist
import Plyr from 'plyr/dist/plyr.js';
import { Storage } from 'aws-amplify';
import { State } from 'vuex-class';
import { Logger } from 'aws-amplify';
const logger = new Logger('ScenarioModal');

@Component({
  components: {
    TimelapseFilter,
    TimelapseExclusion,
    TimelapseSettings,
    S3Image,
  },
})
export default class ScenarioModal extends Vue {
  @Prop({ type: Object, required: true }) private readonly camera!: Camera;
  @Prop({ type: Object, required: true }) private readonly project!: Project;
  @Prop({ type: Boolean, default: true }) readonly withinInterval!: boolean;
  @Prop({ type: Object, default: null }) private readonly scenario!: Scenario | null;
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Prop({ default: true }) readonly showLastDiv!: boolean;

  @Ref('scenario-modal') private modal!: BModal;

  @Action('loadScenario', { namespace: 'project' }) private loadScenario!: () => Promise<void>;
  @Action('createScenario', { namespace: 'project' }) private createScenario!: (
    config: ScenarioConfigurationCreateRequest
  ) => Promise<void>;
  @Action('updateScenario', { namespace: 'project' }) private updateScenario!: (
    config: ScenarioConfigurationUpdateRequest
  ) => Promise<void>;
  @Action('loadTimelapseConfiguration', { namespace: 'project' }) private loadTimelapseConfiguration!: (
    cameraId: string
  ) => Promise<TimelapseConfiguration>;

  @State('user', { namespace: 'profile' }) private user!: User;

  private formatDateFromObject(dateObject: Date): string {
    const year = dateObject.getFullYear();
    let month = '' + (dateObject.getMonth() + 1), // Les mois sont indexés de 0
      day = '' + dateObject.getDate();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  private now = new Date();
  private working = false;
  private startDate = '';
  private endDate = '';
  private visible = false;
  private visible1 = true;
  private periodicity = 'Mensuel';

  public selectedRadio = 'ponctuel'; // valeur par défaut car flexRadioDefault2 est coché

  private defaultImageModifier = {
    iso: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    white: 0,
    crop: Crop.original,
    erigeWatermark: true,
    watermarkPosition: Position.bottomright,
  };

  private exposureRange = { min: 0, max: 250 };
  private gainRange = { min: 0, max: 1800 };
  private sharpRange = { min: 0, max: 60 };
  private personRange = { min: 0, max: 100 };
  private goutteRange = { min: 0, max: 100 };
  private movementRange = { min: 0, max: 100 };

  get plyrOptions(): any {
    const $t = this.$t.bind(this);

    return {
      autoplay: false,
      clickToPlay: true,
      hideControls: true,
      seekTime: 10,
      resetOnEnd: true,
      disableContextMenu: false,
      loadSprite: true,
      iconPrefix: 'plyr',
      iconUrl: 'https://cdn.plyr.io/3.8.3/plyr.svg',
      blankVideo: 'https://cdn.plyr.io/static/blank.mp4',
      controls: [
        'play-large',
        'restart',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        'mute',
        'volume',
        'captions',
        'settings',
        'pip',
        'airplay',
        //'download',
        'fullscreen',
      ],
      tooltips: {
        controls: true,
        seek: true,
      },
      i18n: {
        restart: $t('video.restart').toString(),
        rewind: $t('video.rewind').toString(),
        play: $t('video.play').toString(),
        pause: $t('video.pause').toString(),
        fastForward: $t('video.fastForward').toString(),
        seek: $t('video.seek').toString(),
        seekLabel: $t('video.seekLabel').toString(),
        played: $t('video.played').toString(),
        buffered: $t('video.buffered').toString(),
        currentTime: $t('video.currentTime').toString(),
        duration: $t('video.duration').toString(),
        volume: $t('video.volume').toString(),
        mute: $t('video.mute').toString(),
        unmute: $t('video.unmute').toString(),
        enableCaptions: $t('video.enableCaptions').toString(),
        disableCaptions: $t('video.disableCaptions').toString(),
        settings: $t('video.settings').toString(),
        speed: $t('video.speed').toString(),
        normal: $t('video.normal').toString(),
        quality: $t('video.quality').toString(),
        loop: $t('video.loop').toString(),
        enterFullscreen: $t('video.enterFullscreen').toString(),
        exitFullscreen: $t('video.exitFullscreen').toString(),
        download: $t('video.download').toString(),
        reset: $t('common.reset').toString(),
        disabled: $t('global.need_video').toString(),
        enabled: $t('global.activate').toString(),
        all: $t('global.timelapse').toString(),
        start: $t('global.start').toString(),
        end: $t('global.end').toString(),
      },
    };
  }

  private defaultConfig: TimelapseConfiguration = {
    ...this.defaultImageModifier,
    interval: {
      begin: new Date().getTime(), // Date actuelle en millisecondes
      end: (() => {
        const futureDate = new Date(); // Nouvelle instance de Date
        futureDate.setMonth(futureDate.getMonth() + 1); // Ajouter un mois
        return futureDate.getTime(); // Retourner le timestamp
      })(),
    },
    lastNDays: false,
    lastNDaysSpan: 0,
    period: {
      startHour: '00:00',
      endHour: '23:59',
    },
    exclude: [],
    days: [0, 1, 2, 3, 4, 5, 6],
    fps: 25,
    hdr: false,
    blue: [0, 6],
    gain: [0, 400],
    exposure: [0, 50],
    clearness: [0, 60],
    person: [0, 100],
    movement: [0, 100],
    goutte: [0, 100],
    photoExclusion: [] as string[],
    slowmotion: true,
    outputWidth: 3000,
  };
  private config: TimelapseConfiguration = Object.assign({}, this.defaultConfig);
  private datebegin = this.config.interval.begin;
  private dateend = this.config.interval.end;

  private videoUrl: string | null = null;
  private player: any = null;
  private scenarioName: string = this.scenario ? this.scenario.name : '';

  private async safeInitPlayer(id: string) {
    if (!this.videoUrl) return;

    // Évite les doublons
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    await this.$nextTick();

    const ref = this.$refs[`videoPlayer-${id}`] as HTMLVideoElement | HTMLVideoElement[] | undefined;
    const el = Array.isArray(ref) ? ref[0] : ref;

    if (!el) return;

    this.player = new Plyr(el, this.plyrOptions);

    this.player.source = {
      type: 'video',
      sources: [
        {
          src: this.videoUrl,
          type: 'video/mp4',
        },
      ],
    };
  }

  public mounted(): void {
    this.startDate = this.formatDateFromObject(this.camera.createdAt);
    if (this.project && this.project.endDate) {
      // Convertir la date ISO en format YYYY-MM-DD
      this.endDate = this.project.endDate.split('T')[0];
    } else {
      // Utiliser la date actuelle si endDate n'est pas disponible
      this.endDate = this.formatDateFromObject(new Date());
    }
    logger.info('mounted');
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  private async loadTimelapseExclusionValues(): Promise<void> {
    try {
      // Récupérer la configuration de la caméra depuis DynamoDB
      const config = await this.loadTimelapseConfiguration(this.project.id);

      // Appliquer les valeurs aux sliders de la partie exclusion
      if (config) {
        const newExposure = config.exposure && config.exposure.length === 2 ? config.exposure : [0, 250];
        const newGain = config.gain && config.gain.length === 2 ? config.gain : [0, 1800];
        const newClearness = config.clearness && config.clearness.length === 2 ? config.clearness : [0, 60];
        const newPerson = config.person && config.person.length === 2 ? config.person : [0, 100];
        const newMovement = config.movement && config.movement.length === 2 ? config.movement : [0, 100];
        const newGoutte = config.goutte && config.goutte.length === 2 ? config.goutte : [0, 100];

        // Mettre à jour la configuration de manière réactive
        this.$set(this.config, 'exposure', newExposure);
        this.$set(this.config, 'gain', newGain);
        this.$set(this.config, 'clearness', newClearness);
        this.$set(this.config, 'person', newPerson);
        this.$set(this.config, 'movement', newMovement);
        this.$set(this.config, 'goutte', newGoutte);

        // Forcer le rendu des sliders
        this.$nextTick(() => {
          this.$forceUpdate();
        });

        console.log('Values loaded from DynamoDB TimelapseConfiguration:', {
          exposure: newExposure,
          gain: newGain,
          clearness: newClearness,
          person: newPerson,
        });
      } else {
        console.warn('No timelapse configuration found in DynamoDB for this camera.');
      }
    } catch (error) {
      console.error('Error loading timelapse configuration from DynamoDB:', error);
    }
  }

  public beforeDestroy(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  private isConverted = false;

  public show(config?: TimelapseConfiguration): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Réinitialiser à minuit pour une date propre
    this.datebegin = today.getTime(); // Timestamp en millisecondes

    // Définir dateend à aujourd'hui + 1 mois
    const nextMonth = new Date(today); // On part de today pour garantir +1 mois exact
    nextMonth.setMonth(nextMonth.getMonth() + 1); // Ajouter 1 mois
    this.dateend = nextMonth.getTime(); // Timestamp en millisecondes

    // Mettre à jour this.config avec les nouvelles dates
    this.config = {
      ...this.defaultConfig, // Garder les valeurs par défaut pour les autres propriétés
      interval: {
        begin: this.datebegin,
        end: this.dateend,
      },
    };

    // Si un config est passé, on le fusionne mais on garde nos dates
    if (config) {
      this.config = {
        ...config,
        interval: {
          begin: this.datebegin,
          end: this.dateend,
        },
      };
    } else if (this.scenario !== null) {
      this.config = Object.assign({}, this.scenario.video.timelapseConfiguration);
      // Convertir en millisecondes si nécessaire
      if (this.config.interval.begin < 1000000000000) {
        // Vérifie si la valeur est inférieure à une date en millisecondes
        this.config.interval.begin *= 1000;
      }
      if (this.config.interval.end < 1000000000000) {
        this.config.interval.end *= 1000;
      }
    } else {
      // Définir datebegin à aujourd'hui
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Réinitialiser à minuit
      this.datebegin = today.getTime(); // Timestamp en millisecondes
      // Définir dateend à aujourd'hui + 1 mois
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      this.dateend = nextMonth.getTime(); // Timestamp en millisecondes
      // Initialiser this.config avec les bonnes dates
      this.config = {
        ...this.defaultConfig, // Copier les valeurs par défaut
        interval: {
          begin: this.datebegin,
          end: this.dateend,
        },
      };
      this.scenarioName = '';
    }

    this.modal.show();
  }

  public onShown(): void {
    logger.info('onShown');
    const currentScenario = this.scenario;
    if (currentScenario !== null && currentScenario.video.key !== undefined) {
      Storage.get(currentScenario.video.key)
        .then((url) => {
          this.videoUrl = url as string;
          return this.$nextTick();
        })
        .then(() => {
          this.safeInitPlayer(currentScenario.id);
        })
        .catch((err) => {
          logger.error('Failed to fetch scenario video URL', err);
          this.videoUrl = null;
        });
    }
  }

  public close(): void {
    logger.info('close');
    this.working = false;
    if (this.isConverted) {
      this.config.interval.begin = this.config.interval.begin / 1000;
      this.config.interval.end = this.config.interval.end / 1000;
      this.isConverted = false; // Mark the conversion as done
    }
    this.modal.hide();
  }

  public onHidden(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  public onStartDateSelected(value: number): void {
    console.log('onStartDateSelected appelé avec la valeur:', value);
    // Vérifiez si la valeur est en secondes (inférieure à un seuil raisonnable)
    if (value < 1000000000000) {
      this.datebegin = value * 1000; // Convertir en millisecondes
    } else {
      this.datebegin = value; // Déjà en millisecondes
    }
  }

  public onEndDateSelected(value: number): void {
    console.log('onEndDateSelected appelé avec la valeur:', value);
    if (value < 1000000000000) {
      this.dateend = value * 1000;
    } else {
      this.dateend = value;
    }
    console.log('dateend mis à jour:', this.dateend);
  }

  public save(): void {
    this.working = true;
    const config = { ...this.config };

    // force lastNDays to false before save
    this.config.lastNDays = false;
    if (this.scenario === null) {
      // create

      // Régler les dates de début et de fin pour ce scénario
      config.interval.begin = this.datebegin;
      config.interval.end = this.dateend;
      this.createScenario({
        name: this.scenarioName,
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration: config,
      })
        .then(this.loadScenario)
        .then(this.close);
    } else {
      // update
      config.interval.begin = this.datebegin;
      config.interval.end = this.dateend;
      this.updateScenario({
        name: this.scenarioName,
        scenarioId: this.scenario.id,
        timelapseConfiguration: config,
      })
        .then(this.loadScenario)
        .then(this.close);
    }
  }

  public save1(): void {
    this.working = true;
    const start = new Date(this.startDate);
    const end = new Date(this.endDate);

    switch (this.periodicity) {
      case 'Mensuel':
        this.saveMonthly(start, end);
        break;
      case 'Hebdomadaire':
        this.saveWeekly(start, end);
        break;
      case 'Semestriel':
        this.saveSemester(start, end);
        break;
      case 'Trimestriel':
        this.saveQuarter(start, end);
        break;
    }

    // Fermer la modale après la création
    this.close();
  }

  public saveMonthly(start: Date, end: Date): void {
    this.working = true;
    const monthNames = [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ];

    // Définir le jour du mois au premier du mois pour le début
    start.setDate(1);
    // Créer des scénarios pour chaque mois dans l'intervalle de dates
    const currentMonth = new Date(start);
    while (currentMonth <= end) {
      // Copier la configuration pour ce scénario
      const config = { ...this.config };

      // Régler les dates de début et de fin pour ce scénario
      config.interval.begin = currentMonth.getTime();
      console.log('datebeginmonth', config.interval.begin);
      config.interval.end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getTime();

      const monthName = monthNames[currentMonth.getMonth()];
      const scenarioName = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(
        2,
        '0'
      )}-${monthName}`;

      // Créer le scénario avec le nom généré
      this.createScenario({
        name: scenarioName,
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration: config,
      }).then((newScenario) => {
        this.loadScenario();

        // Émettre un événement personnalisé avec le nouveau scénario
        this.$emit('monthlyScenarioCreated', newScenario);
      });

      // Passer au mois suivant
      currentMonth.setMonth(currentMonth.getMonth() + 1);
      currentMonth.setDate(1); // Assurez-vous que le jour est réglé sur le premier du mois
    }
  }

  public saveWeekly(start: Date, end: Date): void {
    this.working = true;

    // Définir le jour de la semaine au lundi pour le début
    start.setDate(start.getDate() - start.getDay() + 1);

    // Créer des scénarios pour chaque semaine dans l'intervalle de dates
    const currentWeek = new Date(start);
    while (currentWeek <= end) {
      // Copier la configuration pour ce scénario
      const config = { ...this.config };

      // Régler les dates de début et de fin pour ce scénario
      config.interval.begin = currentWeek.getTime();
      config.interval.end = new Date(
        currentWeek.getFullYear(),
        currentWeek.getMonth(),
        currentWeek.getDate() + 6
      ).getTime();

      const scenarioName = `Semaine du ${String(currentWeek.getDate()).padStart(2, '0')}-${String(
        currentWeek.getMonth() + 1
      ).padStart(2, '0')}-${currentWeek.getFullYear()}`;

      // Créer le scénario avec le nom généré
      this.createScenario({
        name: scenarioName,
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration: config,
      }).then((newScenario) => {
        this.loadScenario();

        // Émettre un événement personnalisé avec le nouveau scénario
        this.$emit('weeklyScenarioCreated', newScenario);
      });

      // Passer à la semaine suivante
      currentWeek.setDate(currentWeek.getDate() + 7);
    }
  }

  public saveSemester(start: Date, end: Date): void {
    this.working = true;

    // Créer des scénarios pour chaque semestre dans l'intervalle de dates
    const currentSemester = new Date(start);
    while (currentSemester <= end) {
      // Copier la configuration pour ce scénario
      const config = { ...this.config };

      // Régler les dates de début et de fin pour ce scénario
      config.interval.begin = currentSemester.getTime();
      config.interval.end = new Date(
        currentSemester.getMonth() < 6 ? currentSemester.getFullYear() : currentSemester.getFullYear() + 1,
        currentSemester.getMonth() < 6 ? 5 : 11,
        30
      ).getTime();

      const scenarioName = `${currentSemester.getFullYear()} - ${
        currentSemester.getMonth() < 6 ? '1er' : '2ème'
      } Semestre`;

      // Créer le scénario avec le nom généré
      this.createScenario({
        name: scenarioName,
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration: config,
      }).then((newScenario) => {
        this.loadScenario();

        // Émettre un événement personnalisé avec le nouveau scénario
        this.$emit('semesterScenarioCreated', newScenario);
      });

      // Passer au semestre suivant
      currentSemester.setMonth(currentSemester.getMonth() < 6 ? 6 : 0);
      if (currentSemester.getMonth() === 0) {
        currentSemester.setFullYear(currentSemester.getFullYear() + 1);
      }
    }
  }

  public saveQuarter(start: Date, end: Date): void {
    this.working = true;

    // Aligner le début sur le trimestre le plus proche
    let currentQuarter: Date;
    if (start.getMonth() < 3) {
      currentQuarter = new Date(start.getFullYear(), 0, 1); // 1er janvier
    } else if (start.getMonth() < 6) {
      currentQuarter = new Date(start.getFullYear(), 3, 1); // 1er avril
    } else if (start.getMonth() < 9) {
      currentQuarter = new Date(start.getFullYear(), 6, 1); // 1er juillet
    } else {
      currentQuarter = new Date(start.getFullYear(), 9, 1); // 1er octobre
    }

    while (currentQuarter <= end) {
      // Copier la configuration pour ce scénario
      const config = { ...this.config };
      config.interval.begin = currentQuarter.getTime();

      // Régler les dates de début et de fin pour ce scénario
      let endOfMonth: number;
      switch (currentQuarter.getMonth()) {
        case 0:
          endOfMonth = 31; // Janvier a 31 jours
          break;
        case 3:
          endOfMonth = 30; // Avril a 30 jours
          break;
        case 6:
          endOfMonth = 30; // Juillet a 30 jours
          break;
        case 9:
          endOfMonth = 31; // Octobre a 31 jours
          break;
      }
      config.interval.end = new Date(currentQuarter.getFullYear(), currentQuarter.getMonth() + 3, 0).getTime(); // le 0 va donner le dernier jour du mois précédent

      let quarterName = '';
      switch (currentQuarter.getMonth()) {
        case 0:
          quarterName = '1er';
          break;
        case 3:
          quarterName = '2ème';
          break;
        case 6:
          quarterName = '3ème';
          break;
        case 9:
          quarterName = '4ème';
          break;
      }

      const scenarioName = `${currentQuarter.getFullYear()} - ${quarterName} Trimestre`;

      // Créer le scénario avec le nom généré
      this.createScenario({
        name: scenarioName,
        cameraId: this.camera.id,
        projectId: this.project.id,
        timelapseConfiguration: config,
      }).then((newScenario) => {
        this.loadScenario();

        // Émettre un événement personnalisé avec le nouveau scénario
        this.$emit('quarterScenarioCreated', newScenario);
      });

      // Passer au trimestre suivant
      currentQuarter.setMonth(currentQuarter.getMonth() + 3);
    }
  }

  @Watch('$i18n.locale')
  onLocaleChanged() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.videoUrl && this.scenario) {
      this.safeInitPlayer(this.scenario.id);
    }
  }

  @Watch('scenario')
  public onScenarioChange(val: Scenario | null): void {
    if (val === null) {
      this.scenarioName = '';
    } else {
      this.scenarioName = val.name;
    }
  }
}
